import { Component } from "@angular/core";
import { SplashScreen } from "@capacitor/splash-screen";
import { EventsService } from "./services/event/event.service";
import { Platform } from "@ionic/angular";
import { ConfigService } from "./services/config/config.service";
import { NavigationEnd, NavigationStart, Router } from "@angular/router";
import { WebcontentService } from "./services/webcontent/webcontent.service";
import { SeoService } from "./services/seo/seo.service";
import { filter, pluck, tap } from "rxjs/operators";
import { LogoutService } from "./services/logout/logout.service";
import { checkRedirectLoggedInRoutesName, checkRedirectLoggedOutRoutesName } from "./services/routing/factory";
import { Navigator } from "./services/routing/navigator";
import { StructureService } from "./services/structure/structure.service";
import { RoutingService } from "./services/routing/routing.service";
import { ControlService } from "./services/control/control.service";
import { ControlHelperService } from "./services/control/control-helper.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { CategoryInterface } from "./interfaces/lobby.interface";
import { ROUTE_PATHS } from "src/library/app-routes.lib";
import { GeoLocationStrategyService } from "./services/geo-location-strategy/geo-location-strategy.service";

declare var UserAgent: any

@UntilDestroy()
@Component({
  selector: "app-root",
  templateUrl: "app.component.html",
  styleUrls: ["app.component.scss"],
})
export class AppComponent {

  public categories: CategoryInterface[] = [];
  public isAuthenticated: boolean = false;
  public isMobileApp = false;
  public showBottomBar: boolean = true;
  public showLobby: boolean = true;
  public showNavbars: boolean = true;
  public showSplashAnimation = true;
  public suspendNotifications: boolean = false;
  private isGeoOptimizationEnabled: boolean = false;

  constructor(
    private config: ConfigService,
    private control: ControlService,
    private controlHelper: ControlHelperService,
    private events: EventsService,
    private geoLocationStrategyService: GeoLocationStrategyService,
    private logoutService: LogoutService,
    private navigator: Navigator,
    private platform: Platform,
    private router: Router,
    private routingService: RoutingService,
    private seoService: SeoService,
    private structure: StructureService,
    private webcontentService: WebcontentService,
  ) {
    this.isMobileApp = this.platform.is('capacitor');
  }

  ngOnInit() {
    this.isGeoOptimizationEnabled = this.geoLocationStrategyService.isGeoOptimizationEnabled();
    this.setupIonInputFocusHandler();
    this.browserBackForwardAction();
    //Cookie Consent
    if (!this.isMobileApp) this.addCookieManangement();

    //Dynamically Update Route path title and description
    this.seoService.storeSeoMetaData();
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        pluck('urlAfterRedirects'),
        tap((data: string) => this.seoService.updateMeta(data))
      ).subscribe();


    this.router.events.subscribe(() => {
      if (this.isMobileApp) {
        if (this.router.url.includes('/landing')) {
          this.showBottomBar = false;
          this.showNavbars = false;
        } else {
          this.showBottomBar = true;
          this.showNavbars = true;
        }
      }
    });

    this.config.isLoaded().then(() => {
      const isIPad = this.platform.is('capacitor') && this.platform.is('ipad');
      const userAgentEnabled = this.config.getConfig()["FEATURE_IPAD_USERAGENT_DISABLED"] == "1" ? true : false;
      if (isIPad && !userAgentEnabled) {
        setTimeout(() => {
          SplashScreen.hide();
        }, 2000);
      } else {
        SplashScreen.hide();
      }
    })

    this.handleEventsSubscription();
    this.setUserAgentForiPad();
  }

  private handleEventsSubscription(): void {
    const eventMap = {
      'event:login': (data: any) => this.isAuthenticated = !!data,
      'event:bottommenu:hide': () => { this.showBottomBar = false; this.showNavbars = false; },
      'event:bottommenu:show': () => {
        this.showBottomBar = true;
      },
      'event:siteheader:show': () => this.showNavbars = true,
      'event:siteheader:hide': () => this.showNavbars = false,
      'event:lobby:hide': () => { this.showLobby = false; this.showNavbars = false; },
      'event:lobby:show': () => { this.showLobby = true; this.showNavbars = true; },
      'event:notification:suspend': () => this.suspendNotifications = true,
      'event:notification:resume': () => this.suspendNotifications = false,
    };

    for (const [event, handler] of Object.entries(eventMap)) {
      this.events.subscribe(event, (...args: any[]) => {
        //console.log(`[App Events Sunscriptions]: ${event}`, ...args);
        (handler as (...a: any[]) => void)(...args);
      });
    }
  }


  async setUserAgentForiPad() {
    const isIPad = this.platform.is('capacitor') && this.platform.is('ipad');
    const userAgentEnabled = this.config.getConfig()["FEATURE_IPAD_USERAGENT_DISABLED"] == "1" ? true : false;
    if (isIPad && !userAgentEnabled && UserAgent) {
      try {
        const userAgent = 'Mozilla/5.0 (iPad; CPU OS 13_4 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/13.1 Mobile/15E148 Safari/604.1'
        UserAgent.set(userAgent);
      } catch (error) { }
    }
  }

  onSplashComplete() {
    setTimeout(() => {
      this.showSplashAnimation = false;
    }, 2000);
  }

  mainMenuClosed() {
    this.events.publish("hide:balanceSection");
  }

  menuWillClose() {
    this.events.publish('event:menu:will:close');
  }

  mainMenuOpened() {
    this.events.publish('menuopen');
  }

  menuWillOpen() {
    this.events.publish('event:menu:will:open');
  }

  addCookieManangement() {
    let onetrust_url = this.config.getConfig().FEATURE_ONETRUST_URL;
    let onetrust_domain = this.config.getConfig().FEATURE_ONETRUST_DATA;
    if (onetrust_url && onetrust_domain) {
      const scriptFirst = document.createElement("script");
      scriptFirst.setAttribute("type", "text/javascript");
      scriptFirst.setAttribute("charset", "UTF-8");
      scriptFirst.setAttribute("src", onetrust_url);
      scriptFirst.setAttribute("data-domain-script", onetrust_domain);
      document.head.appendChild(scriptFirst);

      const scriptSecond = document.createElement('script');
      scriptSecond.setAttribute("type", "text/javascript");
      scriptSecond.innerHTML = `function OptanonWrapper() { }`;
      document.head.appendChild(scriptSecond);
    }
  }

  //Used to realign the cursor to proper scrolled position when virtual keyboard is opened
  private setupIonInputFocusHandler() {
    // Listen for the ionFocus event on the document level
    document.addEventListener('ionFocus', () => {
      if (this.platform.is('capacitor') || this.platform.is('mobileweb')) {
        // Get all IonContent elements on the page
        const contentElements = document.querySelectorAll('ion-content');
        if (contentElements.length > 0) {
          // For each IonContent, try to scroll 1px(Not noticeable to user)
          contentElements.forEach(element => {
            const ionContent = (element as any).componentOnReady ? element : null;
            if (ionContent) {
              try {
                ionContent.scrollByPoint && ionContent.scrollByPoint(0, 1, 10);
              } catch (e) {
                console.log('Error scrolling content:', e);
              }
            }
          });
        }
      }
    }, true);
  }

  async fetchLobbyCategory(): Promise<void> {
    this.categories = await new Promise<CategoryInterface[]>((resolve) => {
      this.structure.getCategoriesByPlacement('MAIN')
        .pipe(
          filter(catList => catList?.length > 0),
          untilDestroyed(this))
        .subscribe(resolve);
    });
  }

  browserBackForwardAction() {
    if (!this.isMobileApp) {
      this.router.events.forEach(async (event) => {
        if (event instanceof NavigationStart) {
          if (event.navigationTrigger === 'popstate') {
            let browserURLPath = event.url;
            let authCheckForUser: boolean;
            if (localStorage.getItem('userSession.sessionToken') !== null) {
              authCheckForUser = true;
              if (localStorage.getItem('userSession.mfa') == 'true') return this.logoutService.callLogout({ code: 'INVALID_SESSION_TOKEN', reason: 'PLAYER_INCOMPLETE_MFA' });
            } else {
              authCheckForUser = false;
            }
            let urlParts = browserURLPath.split("/");
            if (browserURLPath != '/' && browserURLPath.indexOf(ROUTE_PATHS.LOBBY) > -1) {
              await this.fetchLobbyCategory();
              let urlToMatch = urlParts[2];
              if (this.config.getState() === 'Pennsylvania' || this.config.getState() === 'PA') {
                urlToMatch = urlParts[3]; //because of additional part in url "valley-forge"
              }
              const matched = this.categories
                .filter((cat: CategoryInterface) => !!cat.category_route)
                .find((cat: CategoryInterface) => cat.category_route === urlToMatch);
              if (matched) {
                this.controlHelper.setActiveLobbyCategory(matched);
                return this.control.selectCategory(matched);
              }
              return this.control.goToDefaultCategory();
            }
            //WordPress page logic
            if (browserURLPath != '/' && urlParts[1] == ROUTE_PATHS.ARTICLE) {
              if (typeof urlParts[2] === 'string') {
                this.webcontentService.updateWebcontentLink(urlParts[2]);
                //this.events.publish("event:reload:externallinks",urlParts[2] );
                return this.routingService.toWebviewTab(true, urlParts[2]);
              }
            }

            if (browserURLPath != '/' && urlParts[1] == ROUTE_PATHS.PROMOTIONS) {
              return this.routingService.toPromotionsTab();
            }

            //Fixing Modal component Back button with minimal code change while user is on Game Tab
            if (this.navigator.getCurrentActiveLobby() == 'game') return;

            //All other modal component logic based on user authentication
            if (browserURLPath != "/") {
              let cleanPath = browserURLPath.substring(1);
              let pageToLoad;
              if (authCheckForUser) {
                pageToLoad = checkRedirectLoggedInRoutesName(cleanPath);
                if (!!pageToLoad) return this.navigator.navigateTo(pageToLoad);
                return this.router.navigate(['/casino'], { skipLocationChange: false });
              } else {
                pageToLoad = checkRedirectLoggedOutRoutesName(cleanPath);
                if (!!pageToLoad) return this.navigator.navigateTo(pageToLoad);
                return this.router.navigate(['/casino'], { skipLocationChange: false });
              }
            }
          }
        }
      });
    }
  }
}
